import {
  CategoryAvailabilityTimeSlot,
  WooCommerceProductCategory
} from '../../core/interfaces/woo-commerce/api/products/product-category-api.interface';
import { WooCommerceCartItem } from '../../core/interfaces/woo-commerce/api/cart/cart.interface';
import { WooCommerceProduct } from '../../core/interfaces/woo-commerce/api/products/product-api.interface';
import {
  WooCommerceProductCategoryObject,
  WooCommerceProductsObject
} from '../state/restaurant/restaurant.state';
import { getWeekdayName } from './date.utils';
import { parseTimeRange } from './time.util';

export interface TimeWindowMinutes {
  start: number;
  end: number;
}

export interface UnavailableCartItemInfo {
  key: string;
  name: string;
  unavailableCategories: string[];
}

function getDayConfig(
  category: WooCommerceProductCategory,
  date: Date,
  timezone?: string
): CategoryAvailabilityTimeSlot | null {
  if (!category.hmp_category_availability) {
    return null;
  }

  const weekdayName = getWeekdayName(date, timezone).toLowerCase();
  const availability = category.hmp_category_availability.weekdays;
  return (
    (availability as Record<string, CategoryAvailabilityTimeSlot>)[
      weekdayName
    ] || null
  );
}

export function isCategoryEnabledForWeekday(
  category: WooCommerceProductCategory,
  date: Date,
  timezone?: string
): boolean {
  if (!category.hmp_category_availability) {
    return true;
  }

  const dayConfig = getDayConfig(category, date, timezone);
  return !!dayConfig && !!dayConfig.enabled;
}

export function getCategoryTimeWindow(
  category: WooCommerceProductCategory,
  date: Date,
  timezone?: string
): TimeWindowMinutes | null | undefined {
  if (!category.hmp_category_availability) {
    return null;
  }

  const dayConfig = getDayConfig(category, date, timezone);
  if (!dayConfig || !dayConfig.enabled) {
    return undefined;
  }

  return {
    start: Number(dayConfig.from_hours) * 60 + Number(dayConfig.from_mins),
    end: Number(dayConfig.to_hours) * 60 + Number(dayConfig.to_mins)
  };
}

export function isCategoryAvailableAtDateTime(
  category: WooCommerceProductCategory,
  date: Date,
  timezone?: string
): boolean {
  const window = getCategoryTimeWindow(category, date, timezone);
  if (window === null) return true;
  if (window === undefined) return false;

  let currentMinutes: number;
  if (timezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    });
    const parts = formatter.formatToParts(date);
    const hours = parseInt(
      parts.find((part) => part.type === 'hour')?.value || '0',
      10
    );
    const minutes = parseInt(
      parts.find((part) => part.type === 'minute')?.value || '0',
      10
    );
    currentMinutes = hours * 60 + minutes;
  } else {
    currentMinutes = date.getHours() * 60 + date.getMinutes();
  }

  return currentMinutes >= window.start && currentMinutes <= window.end;
}

export function resolveCartCategories(
  cartItems: WooCommerceCartItem[],
  products: WooCommerceProductsObject,
  productCategories: WooCommerceProductCategoryObject
): WooCommerceProductCategory[] {
  const categoryIds = new Set<number>();
  const normalizeName = (value?: string): string =>
    (value || '')
      .replace(/<[^>]*>/g, '')
      .trim()
      .toLowerCase();

  const productMap = new Map<number, WooCommerceProduct>();
  const productNameMap = new Map<string, WooCommerceProduct>();
  Object.values(products).forEach((productList: WooCommerceProduct[]) => {
    productList.forEach((product: WooCommerceProduct) => {
      productMap.set(product.id, product);

      const normalizedName = normalizeName(product.name);
      if (normalizedName && !productNameMap.has(normalizedName)) {
        productNameMap.set(normalizedName, product);
      }
    });
  });

  cartItems.forEach((cartItem) => {
    let product = productMap.get(cartItem.id);
    if (!product) {
      product = productNameMap.get(normalizeName(cartItem.name));
    }

    if (product) {
      product.categories.forEach((category) => categoryIds.add(category.id));
    }
  });

  const categories: WooCommerceProductCategory[] = [];
  categoryIds.forEach((id) => {
    if (productCategories[id]) {
      categories.push(productCategories[id]);
    }
  });

  return categories;
}

export function getCartCategoryTimeWindow(
  categories: WooCommerceProductCategory[],
  date: Date,
  timezone?: string
): TimeWindowMinutes | null | undefined {
  if (categories.length === 0) {
    return null;
  }

  let intersectionStart = 0;
  let intersectionEnd = 24 * 60;
  let hasRestriction = false;

  for (const category of categories) {
    const window = getCategoryTimeWindow(category, date, timezone);
    if (window === undefined) {
      return undefined;
    }

    if (window === null) {
      continue;
    }

    hasRestriction = true;
    intersectionStart = Math.max(intersectionStart, window.start);
    intersectionEnd = Math.min(intersectionEnd, window.end);
  }

  if (!hasRestriction) {
    return null;
  }

  if (intersectionStart > intersectionEnd) {
    return undefined;
  }

  return { start: intersectionStart, end: intersectionEnd };
}

export function isDateAvailableForCartCategories(
  categories: WooCommerceProductCategory[],
  date: Date,
  timezone?: string
): boolean {
  const window = getCartCategoryTimeWindow(categories, date, timezone);
  return window !== undefined;
}

export function isTimeSlotAllowedByCategories(
  slotString: string,
  date: Date,
  categories: WooCommerceProductCategory[],
  timezone?: string
): boolean {
  if (categories.length === 0) return true;

  const window = getCartCategoryTimeWindow(categories, date, timezone);
  if (window === null) return true;
  if (window === undefined) return false;

  if (slotString.toLowerCase() === 'asap') {
    return true;
  }

  const timeRange = parseTimeRange(slotString);
  if (!timeRange) return true;

  const slotStartMinutes = timeRange.start.milliseconds / (60 * 1000);
  const slotEndMinutes = timeRange.end.milliseconds / (60 * 1000);

  return slotStartMinutes < window.end && slotEndMinutes > window.start;
}

function normalizeName(value?: string): string {
  return (value || '')
    .replace(/<[^>]*>/g, '')
    .trim()
    .toLowerCase();
}

function buildProductMaps(products: WooCommerceProductsObject): {
  byId: Map<number, WooCommerceProduct>;
  byName: Map<string, WooCommerceProduct>;
} {
  const byId = new Map<number, WooCommerceProduct>();
  const byName = new Map<string, WooCommerceProduct>();

  Object.values(products).forEach((productList: WooCommerceProduct[]) => {
    productList.forEach((product: WooCommerceProduct) => {
      byId.set(product.id, product);
      const productName = normalizeName(product.name);
      if (productName && !byName.has(productName)) {
        byName.set(productName, product);
      }
    });
  });

  return { byId, byName };
}

function tryParseIsoDate(value: string): Date | null {
  const isoDateMatch = value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);
  if (!isoDateMatch) {
    return null;
  }

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export function isCategoryAvailableForSchedule(
  category: WooCommerceProductCategory,
  scheduleDate: Date,
  scheduleTime: Date | string,
  timezone?: string
): boolean {
  // For scheduled orders we must evaluate category windows against the
  // selected schedule calendar date/time as entered by the user.
  const scheduleWindow = getCategoryTimeWindow(category, scheduleDate);
  if (scheduleWindow === null) {
    return true;
  }
  if (scheduleWindow === undefined) {
    return false;
  }

  if (scheduleTime instanceof Date) {
    const selectedMinutes =
      scheduleTime.getHours() * 60 + scheduleTime.getMinutes();
    return (
      selectedMinutes >= scheduleWindow.start &&
      selectedMinutes <= scheduleWindow.end
    );
  }

  const isoScheduleTime = tryParseIsoDate(scheduleTime);
  if (isoScheduleTime) {
    const selectedMinutes =
      isoScheduleTime.getHours() * 60 + isoScheduleTime.getMinutes();
    return (
      selectedMinutes >= scheduleWindow.start &&
      selectedMinutes <= scheduleWindow.end
    );
  }

  if (scheduleTime.toLowerCase() === 'asap') {
    return isCategoryEnabledForWeekday(category, scheduleDate);
  }

  return isTimeSlotAllowedByCategories(scheduleTime, scheduleDate, [category]);
}

export function getUnavailableCartItemsForSchedule(
  cartItems: WooCommerceCartItem[],
  products: WooCommerceProductsObject,
  productCategories: WooCommerceProductCategoryObject,
  scheduleDate: Date,
  scheduleTime: Date | string,
  timezone?: string
): UnavailableCartItemInfo[] {
  const productMaps = buildProductMaps(products);
  const unavailableItems: UnavailableCartItemInfo[] = [];

  cartItems.forEach((cartItem) => {
    const product =
      productMaps.byId.get(cartItem.id) ||
      productMaps.byName.get(normalizeName(cartItem.name));
    if (!product) {
      return;
    }

    const categories = product.categories
      .map((category) => productCategories[category.id])
      .filter(
        (category): category is WooCommerceProductCategory => category != null
      );
    if (categories.length === 0) {
      return;
    }

    const availableCategories = categories.filter((category) =>
      isCategoryAvailableForSchedule(
        category,
        scheduleDate,
        scheduleTime,
        timezone
      )
    );
    if (availableCategories.length > 0) {
      return;
    }

    unavailableItems.push({
      key: cartItem.key,
      name: cartItem.name,
      unavailableCategories: categories.map((category) => category.name)
    });
  });

  return unavailableItems;
}
